import React, { useEffect, useRef, useState } from 'react';
import { loadAdSenseScript, getAdSlot, isAdSenseConfigured } from '../utils/loadAdSense';
import './AdPlaceholder.css';

const VARIANT_LABELS = {
  banner: '728×90',
  'game-top': '728×90 / 320×50',
  sidebar: '300×250',
  footer: '728×90',
  'mobile-sticky': '320×50 (mobile)',
};

/**
 * Ad slot component – renders Google AdSense when configured, else placeholder.
 * Set REACT_APP_ADSENSE_PUBLISHER_ID and REACT_APP_ADSENSE_SLOT_* in .env
 *
 * Variants: banner | game-top | sidebar | footer | mobile-sticky
 */
export default function AdPlaceholder({ variant = 'banner' }) {
  const containerRef = useRef(null);
  const insRef = useRef(null);
  const slot = getAdSlot(variant);
  const publisherId = process.env.REACT_APP_ADSENSE_PUBLISHER_ID?.trim();
  const isConfigured = isAdSenseConfigured() && slot;
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!isConfigured) return;

    const containerEl = containerRef.current;
    const insEl = insRef.current;
    if (!containerEl || !insEl) return;

    loadAdSenseScript();

    let destroyed = false;
    let resizeObserver = null;
    let rafId = null;
    let timeoutId = null;
    let collapseTimeoutId = null;
    let mutationObserver = null;

    const hasAdAlready =
      insEl.dataset.adsbygoogleStatus != null ||
      insEl.dataset.adPushed === '1' ||
      insEl.getAttribute('data-adsbygoogle-status') != null;

    const detectFilled = () => {
      const status = insEl.dataset.adsbygoogleStatus || insEl.getAttribute('data-adsbygoogle-status');
      if (status != null && status !== '') return true;
      if (insEl.querySelector('iframe, ins, div')) return true;
      const rect = insEl.getBoundingClientRect();
      return rect.height > 0;
    };

    const pushAd = () => {
      if (destroyed) return;
      if (insEl.dataset.adPushed === '1') return;
      if (insEl.dataset.adsbygoogleStatus != null) return;

      const { width } = containerEl.getBoundingClientRect();
      if (!width || width <= 0) return;

      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        insEl.dataset.adPushed = '1';
      } catch (e) {
        // AdSense can throw when an ad is already filled (e.g., React re-render / StrictMode).
        console.warn('AdSense push failed:', e);
      }
    };

    // Always start expanded while we try to fill.
    setCollapsed(false);

    if (!hasAdAlready) {
      // Try immediately and then wait for a measurable width (layout/render, route transitions, etc.)
      pushAd();

      resizeObserver = new ResizeObserver(() => {
        pushAd();
      });
      resizeObserver.observe(containerEl);

      // Fallback retries in case ResizeObserver doesn't fire (rare, but helps on some layouts).
      const retry = (remaining) => {
        if (destroyed || remaining <= 0) return;
        pushAd();
        rafId = requestAnimationFrame(() => retry(remaining - 1));
      };
      rafId = requestAnimationFrame(() => retry(10));
      timeoutId = window.setTimeout(() => pushAd(), 250);
    }

    // Collapse the slot if AdSense doesn't fill (blocked, no fill, etc.)
    // Keep placeholders for non-configured envs, but avoid big empty gaps in production.
    collapseTimeoutId = window.setTimeout(() => {
      if (destroyed) return;
      if (!detectFilled()) setCollapsed(true);
    }, 2500);

    mutationObserver = new MutationObserver(() => {
      if (destroyed) return;
      if (detectFilled()) setCollapsed(false);
    });
    try {
      mutationObserver.observe(insEl, { childList: true, attributes: true, subtree: true });
    } catch {
      // ignore
    }

    // Special-case the mobile sticky slot so we don't reserve padding if it's collapsed.
    if (variant === 'mobile-sticky') {
      const update = () => {
        if (destroyed) return;
        if (detectFilled() && !collapsed) {
          document.documentElement.dataset.mobileAd = '1';
        } else {
          delete document.documentElement.dataset.mobileAd;
        }
      };
      update();
      const id = window.setInterval(update, 500);
      return () => {
        destroyed = true;
        if (resizeObserver) resizeObserver.disconnect();
        if (rafId != null) cancelAnimationFrame(rafId);
        if (timeoutId != null) window.clearTimeout(timeoutId);
        if (collapseTimeoutId != null) window.clearTimeout(collapseTimeoutId);
        if (mutationObserver) mutationObserver.disconnect();
        window.clearInterval(id);
        delete document.documentElement.dataset.mobileAd;
      };
    }

    return () => {
      destroyed = true;
      if (resizeObserver) resizeObserver.disconnect();
      if (rafId != null) cancelAnimationFrame(rafId);
      if (timeoutId != null) window.clearTimeout(timeoutId);
      if (collapseTimeoutId != null) window.clearTimeout(collapseTimeoutId);
      if (mutationObserver) mutationObserver.disconnect();
    };
  }, [isConfigured, slot, variant, collapsed]);

  if (!isConfigured) {
    return (
      <div className={`ad-placeholder ad-placeholder--${variant}`}>
        <span className="ad-placeholder__label">Ad Space</span>
        <span className="ad-placeholder__size">{VARIANT_LABELS[variant] || variant}</span>
      </div>
    );
  }

  return (
    <div
      className={`ad-slot ad-slot--${variant} ${collapsed ? 'ad-slot--collapsed' : ''}`}
      ref={containerRef}
      aria-hidden={collapsed ? 'true' : 'false'}
    >
      <ins
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client={publisherId}
        data-ad-slot={slot}
        data-ad-format="auto"
        data-full-width-responsive="true"
        ref={insRef}
      />
    </div>
  );
}
